// Core packages
import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

// Third party packages
import { formatDate } from '@angular/common';
import { MatDialog } from "@angular/material";
import { Sort } from '@angular/material/sort';
import { TranslateService } from "@ngx-translate/core";
import { Workbook } from 'exceljs';
import * as FileSaver from "file-saver";
import moment from 'moment';
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { take } from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { ExportContractsDialogComponent } from 'src/app/modules/properties/components/export-contracts-dialog/export-contracts-dialog.component';
import { ExportRasDocsDialogComponent } from 'src/app/modules/properties/components/export-ras-docs-dialog/export-ras-docs-dialog.component';
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { RolesService } from "src/app/modules/roles/roles.service";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { PropertyInterface } from "src/app/shared/models/property/property.interface";
import { Property } from "src/app/shared/models/property/property.model";
import { IUser } from "src/app/shared/models/user/user.interface";
import { DateTimeFormatPipe } from "src/app/shared/pipes/date-format.pipe";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { ThemeService } from "src/app/shared/services/theme.service";
import { CondoStructuresService } from "../../condo-structures.service";

@Component({
  selector: "app-property-caretakers",
  templateUrl: "./property-caretakers.page.html",
  styleUrls: ["./property-caretakers.page.scss"]
})
export class PropertyCaretakersComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  loggedUserRole: any;
  currentRole: any;
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  // Could not be "Property" type because in "get" method in the back-end
  // We are populating some fields, so the model is not the same!

  loggedUserRoleType = "";
  public property: Property = new Property();
  propertyData: PropertyInterface;
  propertyId = "";
  loggedUserShared = false;
  loggedUserMaster = false;
  loggedUserAdmin = false;
  dateTimeFormat: DateTimeFormatPipe;
  activeSection: string; // Active document section
  activeSectionData: any[] = [];
  itemId: string; // Id of active mapping section item
  condoStructure: any;
  documentArchive: any;
  documentArchiveId: string;
  sortedDocuments: any[] = [];
  documentArchiveIsLoading = true;

  dipendenti: any[] = [];
  sortedData = [];

  subscriptionType = 'FREE';

  mappaturaOpen = false;

  /**
   * Class constructor
   */
  constructor(
    private configService: ConfigService,
    private activatedRoute: ActivatedRoute,
    private propertiesService: PropertiesService,
    private router: Router,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private authService: AuthService,
    public dialog: MatDialog,
    private cookieService: CookieService,
    private apiService: ApiService, // DO NOT MOVE since it's used for 'autocomplete' field type
    private condoStructuresService: CondoStructuresService,
    private rolesService: RolesService,
    private helperService: HelperService,
  ) {

  }


  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(
      this.translateService.instant("properties.caretakersPage.pageTitle")
    );

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(res => {
        this.loggedUser = res;
        const currentRole = this.loggedUser.userRoles.find((obj: any) => {
          return (
            obj._id.toString() ===
            this.authService.loggedUserRequestedRole$.getValue().toString()
          );
        });
        this.loggedUserRoleType = currentRole.role.type;
      })
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        res => (this.loggedUserPermissions = res)
      )
    );

    // Get logged user's role data
    this.currentRole = this.loggedUser.userRoles.find((obj: any) => {
      return (
        obj._id.toString() ===
        this.authService.loggedUserRequestedRole$.getValue().toString()
      );
    });
    this.loggedUserRole = this.currentRole.role;

    if (this.currentRole.role.type === "shared") {
      this.loggedUserShared = true;
    }

    this.loggedUserMaster =
      this.loggedUserRole.type === "master" ||
      this.loggedUserRole.type === "collaborator";
    this.loggedUserAdmin =
      this.loggedUserRole.type === "admin" ||
      this.loggedUserRole.type === "operator";
    
    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    // Get requested property data and document archive
    this.propertyId = this.activatedRoute.snapshot.params.propertyId;
    this.subscriptions.push(
      this.propertiesService.get(this.propertyId).subscribe(
        res => {
          if (res.status) {
            // Set permission data
            this.property.setData(res.data);
            this.propertyData = res.data;

            if (this.propertyData.subscription
              && this.propertyData.subscription.key) {
              this.subscriptionType = this.propertyData.subscription.key;
            }

            // If property has a structure ID, than retrive related data
            // and document archive
            if (this.propertyData.structure) {
              this.subscriptions.push(
                this.condoStructuresService.get(this.propertyData.structure)
                  .subscribe(res => {
                    if (
                      res.status && 
                      res.data
                    ) {
                      this.mappaturaOpen = res.data.open;
                    }
                  },
                    err => {
                      this.helperService.handleError(err);
                    })
              );

              this.condoStructuresService
                .getCondoStructuresCaretakers(this.property.get("structure"))
                .subscribe(res => {
                  if (res.status) {
                    if (res.data.dipendenti
                      && Array.isArray(res.data.dipendenti)) {
                      res.data.dipendenti.forEach((item: any) => {
                        this.dipendenti.push({
                          tipologia: this.translateService.instant('properties.structures.dipendente'),
                          nomeFornitore: "____",
                          cognomeNome: `${item.cognome} ${item.nome}`,
                          telefonoFisso: item.telefonoFisso,
                          telefonoMobile: item.telefonoMobile,
                          orari: item.orari,
                          email: item.email,
                        });
                      });
                    }
                    if (res.data.impresePulizia
                      && Array.isArray(res.data.impresePulizia)) {
                      res.data.impresePulizia.forEach((item: any) => {
                        this.dipendenti.push({
                          tipologia: this.translateService.instant('properties.structures.impresaEsterna'),
                          nomeFornitore: item.nomeDitta,
                          cognomeNome: item.nomePersona,
                          telefonoFisso: item.telefonoFisso,
                          telefonoMobile: item.telefonoMobile,
                          orari: item.orari,
                          email: item.email,
                        });
                      });
                    }
                    this.sortedData = this.dipendenti.slice();

                    this.documentArchiveIsLoading = false;
                  } else {
                    this.documentArchiveIsLoading = false;
                    this.toastrService.error(res.message);
                  }
                });
            } else {
              this.documentArchiveIsLoading = false;
            }

            return;
          }

          // Something bad happened
          // redirect user to home
          this.router.navigate([this.lang, "app", "properties", "list"]);
        },
        err => {
          // if we came here users with given userId do not exist
          this.router.navigate([this.lang, "errors", "404"]);
        }
      )
    );
  }

  /**
  * Handle component destroy
  *
  * @since 1.0.0
  */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
    * Handle table sort
    *
    * @since 1.0.0
    */
  sortData(sort: Sort): any {
    const data = this.sortedData;
    if (!sort.active || sort.direction === '') {
      this.sortedData = data;
      return;
    }

    this.sortedData = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'tipologia': return this.compare(a.tipologia, b.tipologia, isAsc);
        case 'nomeFornitore': return this.compare(a.nomeFornitore, b.nomeFornitore, isAsc);
        case 'cognomeNome': return this.compare(a.cognomeNome, b.cognomeNome, isAsc);
        case 'telefonoFisso': return this.compare(a.telefonoFisso, b.telefonoFisso, isAsc);
        case 'telefonoMobile': return this.compare(a.telefonoMobile, b.telefonoMobile, isAsc);
        case 'orari': return this.compare(a.orari, b.orari, isAsc);
        case 'email': return this.compare(a.email, b.email, isAsc);
        default: return 0;
      }
    });
  }

  /**
   * Utility per table sort
   *
   * @since 1.0.0
   */
  compare(a: number | string, b: number | string, isAsc: boolean): any {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  /**
   * Handle table sort
   *
   * @since 1.0.0
   */
  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.sortedData = this.dipendenti.slice();
    const regex = new RegExp(filterValue.trim().toLowerCase(), "gi");
    this.sortedData = this.dipendenti.filter((el: any) => {

      const ret = el.tipologia.search(regex) > -1
        || el.nomeFornitore.search(regex) > -1
        || el.cognomeNome.search(regex) > -1
        || el.telefonoFisso.search(regex) > -1
        || el.telefonoMobile.search(regex) > -1
        || el.orari.search(regex) > -1
        || el.email.search(regex) > -1
      return ret;
    })
  }

  onExportToExcel(): void {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Sheet");
    const header = [
      this.translateService.instant('properties.structures.fields.dipendenti.tipologia'),
      this.translateService.instant('properties.structures.fields.dipendenti.nomeFornitore'),
      this.translateService.instant('properties.structures.fields.dipendenti.cognomeNome'),
      this.translateService.instant('properties.structures.fields.dipendenti.telefonoFisso'),
      this.translateService.instant('properties.structures.fields.dipendenti.telefonoMobile'),
      this.translateService.instant('properties.structures.fields.dipendenti.orari'),
      this.translateService.instant('properties.structures.fields.dipendenti.email')
    ];
    worksheet.addRow(header);
    worksheet.lastRow.height = 37.45; // Altezza riga 25
    worksheet.lastRow.alignment = {
      horizontal: "center",
      vertical: "middle",
      wrapText: true,
    };
    worksheet.lastRow.font = {
      size: 7,
    };
    const widths = [30, 12, 20, 20, 15, 15, 20];
    widths.forEach((val, index) => {
      worksheet.getColumn(index + 1).width = val;
    });

    worksheet.getRow(1).eachCell({ includeEmpty: true }, (cell) => {
      worksheet.getCell(cell.address).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF87CEEB" },
      };
    });

    // BEGIN - Print settings
    // Set horizontal page orientation
    worksheet.pageSetup = {
      orientation: "landscape",
      paperSize: 9, // A4
      showGridLines: true,
    };

    // Set page header/footer
    worksheet.headerFooter.oddHeader = `&LArchedi&RStampato il ${moment().format(
      "DD/MM/YYYY"
    )}`;
    worksheet.headerFooter.oddFooter = `&L${this.translateService.instant('properties.structures.dipendentiEImprese')}&RPage &P/&N`;
    // END - Print settings

    let rowCount = 1;
    this.dipendenti.forEach(el => {
      rowCount++;
      const row = worksheet.addRow([
        el.tipologia,
        el.nomeFornitore,
        el.cognomeNome,
        el.telefonoFisso,
        el.telefonoMobile,
        el.orari,
        el.email
      ]);
      worksheet.lastRow.font = {
        size: 7,
      };

      let maxLine = 1;
      for (let i = 1; i <= widths.length; i++) {
        if (i === 1) {
          worksheet.lastRow.getCell(i).alignment = {
            horizontal: "left",
            indent: 0,
            vertical: "middle",
            wrapText: true,
          };
        } else {
          worksheet.lastRow.getCell(i).alignment = {
            horizontal: "left",
            indent: 1,
            vertical: "middle",
            wrapText: true,
          };
        }

        const cellText = worksheet.lastRow.getCell(i).value;
        let value = -1;
        if (cellText) {
          value =
            Math.ceil(
              worksheet.lastRow.getCell(i).value.toString().length /
                widths[i - 1]
            ) - 1;
          const countLine = (
            worksheet.lastRow
              .getCell(i)
              .value.toString()
              .match(/(?:\r\n|\r|\n)/g) || []
          ).length;
          if (countLine > value) {
            value = countLine;
          }
        }
        if (value > maxLine) {
          maxLine = value;
        }
        worksheet.lastRow.height = 22.45 * maxLine; // 22.45 = altezza riga 15
      }
    });

    // Save to file
    const date = formatDate(new Date(), "yyyyMMddhhmmss", this.lang);
    const fileName = `${this.translateService.instant(
      "properties.structures.fields.dipendenti.exportFileName"
    )} ${date}.xlsx`;
    workbook.xlsx.writeBuffer().then((data) => {
      const blob = new Blob(
        [data], 
        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
      );
      FileSaver.saveAs(blob, fileName);
    });

  }

  /**
   * Export properties in a file with given format
   *
   * @since 1.0.0
   */
  onExport(target?: string, propertyId?: string): void {
    // If target is not provided, than just export all properties
    if (!target) {
      const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
      const fileName = `${this.translateService.instant(
        "properties.list.exportFileName"
      )} ${date}`;
      this.subscriptions.push(
        this.propertiesService.getExport().subscribe(
          res => {
            const fileBlob = res;
            const blob = new Blob([fileBlob], {
              type: "application/vnd.ms-excel"
            });
            FileSaver.saveAs(blob, `${fileName}.xlsx`);
          },
          err => {
            this.helperService.handleError(err);
          }
        )
      );
    } else if (target && typeof propertyId !== "undefined") {
      // Handle export for missing/inserted documents
      const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
      const fileName = `${this.translateService.instant(
        `properties.list.${target}`
      )} ${date}`;
      this.subscriptions.push(
        this.propertiesService.getDocExport(target, propertyId).subscribe(
          res => {
            const fileBlob = res;
            const blob = new Blob([fileBlob], {
              type: "application/vnd.ms-excel"
            });
            FileSaver.saveAs(blob, `${fileName}.xlsx`);
          },
          err => {
            this.helperService.handleError(err);
          }
        )
      );
    }
  }
      
  /**
   * Prevent redirect to archive if structure is open
   *
   * @since 1.0.0
   */
   onGoToDocumentArchive(): void {
    if (!this.mappaturaOpen) {
      return;
    }
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('dialogs.confirm.mappaturaOpen.title'),
        message: this.translateService.instant('dialogs.confirm.mappaturaOpen.message'),
        btnCancelText: this.translateService.instant('common.cta.close'),
        messageOnly: true,
      },
      width: '500px',
      disableClose: false,
    });
  }
  
  /**
   * Word contracts export
   *
   * @since 1.0.0
   */
  onExportContracts(): void {
    const dialogRef = this.dialog.open(ExportContractsDialogComponent, {
      minWidth: "50%",
      maxHeight: "98vh",
      data: {
        propertyId: this.propertyId,
      },
      disableClose: true,
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        return;
      });
  }
  
  /**
   * RAS docs export in PDF format
   *
   * @since 1.0.0
   */
  onExportRAS(): void {
    const dialogRef = this.dialog.open(ExportRasDocsDialogComponent, {
      width: "700px",
      data: {
        propertyId: this.propertyId,
      },
      disableClose: true,
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }

        return;
      });
  }
}